import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { api } from "../lib/api";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Warning } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const t = useT();
  const [sp] = useSearchParams();
  const adminMode = sp.get("role") === "admin";
  const [email, setEmail] = useState(adminMode ? "admin@zcardon.com" : "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [gate, setGate] = useState({ open: true, message: null, loaded: false });

  useEffect(() => {
    api.get("/public/signin-status")
      .then((r) => setGate({ ...r.data, loaded: true }))
      .catch(() => setGate({ open: true, message: null, loaded: true }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(t("login.toast_welcome", { name: u.full_name.split(" ")[0] }));
      nav(u.role === "admin" ? "/admin" : u.role === "manager" ? "/manager" : u.role === "agent" ? "/agent" : "/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 503) {
        setGate({ open: false, message: detail, loaded: true });
      }
      toast.error(detail || t("login.toast_failed"));
    } finally { setLoading(false); }
  };

  const showCustomerGateBanner = gate.loaded && !gate.open;

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <div className="max-w-md mx-auto px-6 py-20">
        {adminMode && (
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[#184A30] bg-[#184A30]/10 px-3 py-1.5 rounded-full mb-4" data-testid="admin-badge">
            <ShieldCheck size={14} weight="duotone" /> {t("login.admin_badge")}
          </div>
        )}
        <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50] mb-3">
          {adminMode ? t("login.eyebrow.admin") : t("login.eyebrow.welcome")}
        </p>
        <h1 className="serif text-5xl">{adminMode ? t("login.title_admin") : t("login.title")}</h1>
        {showCustomerGateBanner && (
          <div
            className="mt-8 rounded-2xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-5"
            data-testid="login-maintenance-banner"
          >
            <div className="flex items-start gap-3">
              <Warning size={20} weight="duotone" className="text-[#8C6B21] shrink-0 mt-0.5" />
              <div className="text-sm text-[#8C6B21]">
                <p className="font-semibold">{t("login.gate_title")}</p>
                <p className="mt-1 text-[#4A5C50]">
                  {gate.message || t("login.gate_default_message")}
                </p>
                <p className="mt-3 text-[12px] text-[#4A5C50]">
                  {t("login.gate_admin_note")}
                </p>
              </div>
            </div>
          </div>
        )}
        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="login-form">
          <div>
            <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">{t("login.email_label")}</Label>
            <input className="underline-input mono" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">{t("login.password_label")}</Label>
            <input className="underline-input mono" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white" data-testid="login-submit">
            {loading ? t("login.submitting") : t("login.submit")}
          </Button>
          <div className="text-right">
            <Link to="/forgot-password" className="text-xs text-[#4A5C50] link-ul" data-testid="login-forgot-password-link">
              {t("login.forgot_password")}
            </Link>
          </div>
        </form>
        <p className="text-sm text-[#4A5C50] mt-8">
          {adminMode
            ? <>{t("login.customer_q")} <Link to="/login" className="text-[#184A30] link-ul">{t("login.regular_signin")}</Link></>
            : <>{t("login.new_here")} <Link to="/register" className="text-[#184A30] link-ul" data-testid="link-register">{t("login.create_account")}</Link></>}
        </p>
        <div className="mt-10 p-4 border border-[#E5E0D8] rounded-xl text-xs text-[#4A5C50] bg-white">
          <p className="font-semibold mb-2 text-[#112318]">{t("login.demo_title")}</p>
          <p>admin@zcardon.com / SunIce#8679</p>
          <p>manager@zcardon.com / Manager@2026</p>
          <p className="text-[#8C6B21] mt-1 italic">{t("login.demo_customers_note")}</p>
        </div>
      </div>
    </div>
  );
}
